import {
  FilteringState,
  IntegratedFiltering,
  IntegratedSorting,
  Filter,
  SearchState,
  SelectionState,
  SortingState,
} from '@devexpress/dx-react-grid'
import {
  Grid as TableGrid,
  SearchPanel,
  TableFilterRow,
  TableHeaderRow,
  TableSelection,
  Toolbar,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui'
import { Theme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'
import { UnitMaster } from '../../Units/reducer'
import {
  getUnitsCancel,
  getUnitsIndividual,
  getUnitsMaster,
} from '../../Units/selectors'
import { buildUnitOperatorStatus } from '../../Units/cancellationStatus'
import { CustomTableCell } from '../Shared/cell'
import { COLUMNS } from './config'
import { AdminIndividualUnitModal } from './Modal'

const toLowerCase = (value: string | number) => String(value).toLowerCase()

const booleanPredicate = (value: any, filter: Filter, row: any) => {
  const filterValue = toLowerCase(filter.value)
  if (filterValue.startsWith('y') && value === true) return true
  return filterValue.startsWith('n') && value === false
}

const getRowId = (row: UnitMaster) => `${row.id}`

interface Props {
  tableHeight?: number | string
  minHeight?: number
}

export const AdminIndividualTableComponent: React.FC<Props> = ({
  tableHeight = '100%',
  minHeight = 0,
}) => {
  const [selectedRow, setSelectedRow] = React.useState<UnitMaster | null>(null)
  const [unit, setUnit] = React.useState<UnitMaster | null>(null)

  const masterUnits = useSelector(getUnitsMaster)
  const individualUnits = useSelector(getUnitsIndividual)
  const cancelUnits = useSelector(getUnitsCancel)

  const _individualUnits = React.useMemo(
    () =>
      individualUnits.map((iu) => {
        const _obj = masterUnits.find((mu) => mu.id === iu.masterId)
        if (_obj != null) {
          return {
            ..._obj,
            ...iu,
            isAvailable: Boolean(!iu.customerProfileId),
            statusLabel: buildUnitOperatorStatus({
              unit: iu,
              cancelUnits,
            }),
          }
        }
        return {
          ...iu,
          ...{
            isAvailable: Boolean(!iu.customerProfileId),
            statusLabel: buildUnitOperatorStatus({
              unit: iu,
              cancelUnits,
            }),
          },
        }
      }),
    [cancelUnits, individualUnits, masterUnits]
  )

  const onSelectRow = React.useCallback(
    async (selection: Array<number | string>) => {
      const row = _individualUnits.find((iu) => iu.id === String(selection))

      if (row?.customerProfileId) {
        setSelectedRow(row)
        setUnit(row)
      }
    },
    [_individualUnits]
  )

  const onCloseUnitModal = React.useCallback(() => {
    setSelectedRow(null)
    setUnit(null)
  }, [])

  const onUnitChanged = React.useCallback((nextUnit: UnitMaster) => {
    setSelectedRow(nextUnit)
    setUnit(nextUnit)
  }, [])

  return (
    <Paper
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: (theme: Theme) => alpha(theme.palette.background.paper, 0.72),
      }}
    >
      <Box
        sx={{
          py: 1,
          px: 2,
          bgcolor: (theme: Theme) => theme.palette.info.dark,
        }}
      >
        <Typography variant="h6">All Units</Typography>
      </Box>
      <Paper
        sx={{
          height: tableHeight,
          flex: 1,
          minHeight,
          '& > div': {
            height: '100%',
          },
        }}
      >
        <TableGrid
          rows={_individualUnits}
          columns={COLUMNS}
          getRowId={getRowId}
        >
          <SelectionState selection={[]} onSelectionChange={onSelectRow} />
          <SearchState defaultValue="" />
          <FilteringState />
          <SortingState
            defaultSorting={[
              {
                columnName: 'size',
                direction: 'desc',
              },
              {
                columnName: 'unitNumber',
                direction: 'desc',
              },
            ]}
          />
          <IntegratedFiltering
            columnExtensions={[
              {
                columnName: 'isAvailable',
                predicate: booleanPredicate,
              },
              {
                columnName: 'isClimate',
                predicate: booleanPredicate,
              },
              {
                columnName: 'isCovered',
                predicate: booleanPredicate,
              },
            ]}
          />
          <IntegratedSorting />
          <VirtualTable cellComponent={CustomTableCell} />
          <TableHeaderRow showSortingControls />
          <TableSelection selectByRowClick showSelectionColumn={false} />
          <TableFilterRow />
          <Toolbar />
          <SearchPanel />
        </TableGrid>
      </Paper>
      {selectedRow != null && (
        <AdminIndividualUnitModal
          onClose={onCloseUnitModal}
          selectedRow={selectedRow}
          individualUnits={individualUnits}
          unit={unit}
          onUnitChanged={onUnitChanged}
        />
      )}
    </Paper>
  )
}
